'use client';
import { useStaticContent } from '@providers/SiteDataContextProvider';
import { type JSX, useMemo } from 'react';
import type { SiteContentRendererProps } from 'src/types';

const getNestedValue = (obj: any, path: string): string => {
  return path.split('.').reduce((current, key) => {
    return current?.[key];
  }, obj);
};

const parseHtmlContent = (content: string): JSX.Element => {
  const parts = content.split(/(<mark>.*?<\/mark>|<br\/?>)/g);

  return (
    <>
      {parts.map((part, index) => {
        if (part.startsWith('<mark>') && part.endsWith('</mark>')) {
          const text = part.replace(/<\/?mark>/g, '');
          return <mark key={index}>{text}</mark>;
        }

        if (part === '<br>' || part === '<br/>') {
          return <br key={index} />;
        }

        return part || null;
      })}
    </>
  );
};

export const ClientSiteContentRenderer = ({
  path,
  fallback = '',
  className,
  as: Component = 'span',
}: SiteContentRendererProps) => {
  const staticContent = useStaticContent();

  const content = useMemo(() => {
    const value = getNestedValue(staticContent, path);
    return value || fallback;
  }, [staticContent, path, fallback]);

  const renderedContent = useMemo(() => {
    if (!content) return null;

    const hasHtmlTags =
      /<(mark|br\/?)\b[^<]*(?:(?!<\/\1>)<[^<]*)*<\/\1?>|<br\/?>/gi.test(
        content,
      );

    if (hasHtmlTags) {
      return parseHtmlContent(content);
    }

    return content;
  }, [content]);

  return <Component className={className}>{renderedContent}</Component>;
};
